import * as React from "react";
import { DeploymentStepResource, DeploymentActionResource, Permission } from "client/resources";
import FilterSearchBox from "components/FilterSearchBox";
import Divider from "components/Divider";
import { DoBusyTask, DataBaseComponent, DataBaseComponentState } from "components/DataBaseComponent/DataBaseComponent";
import SpecialVariables from "client/specialVariables";
import routeLinks from "routeLinks";
import DeploymentPartForSidebar from "./DeploymentPartForSidebar";
import pluginRegistry, { ActionScope } from "components/Actions/pluginRegistry";
import getActionLogoUrl from "../getActionLogoUrl";
import FormPaperLayout from "components/FormPaperLayout";
import { PermissionCheck } from "components/PermissionCheck";
import ActionButton, { NavigationButton, NavigationButtonType, ActionButtonType } from "components/Button";
import OverflowMenu, { OverflowMenuItems } from "components/Menu/OverflowMenu";
import DeploymentPartSorter from "./DeploymentPartSorter";
import { RouteComponentProps } from "react-router";
import Section from "components/Section";
import DeploymentPartContextMenu from "./DeploymentPartContextMenu";
import { NoResults } from "components/NoResults/NoResults";
import Logger from "client/logger";
import { withProjectContext, WithProjectContextInjectedProps, useProjectContext, ActionContextProvider } from "../../context";
import { DeploymentProcessContextProvider, DeploymentProcessEditorFilter, useDeploymentProcessContext } from "./DeploymentProcessContext";
import { Note } from "components/form";
import ActionTemplateSearchResource from "client/resources/actionTemplateSearchResource";

const styles = require("./DeploymentProcessEditor.less");
const deploymentPartForSidebarStyles = require("./DeploymentPartForSidebar.less");
const rollingStep = require("./step-rolling.svg");

export interface DeploymentProcessEditorRenderProps {
    step: DeploymentStepResource;
}

interface DeploymentProcessEditorState extends DataBaseComponentState {
    redirectTo: string;
}

interface DeploymentProcessEditorProps {
    render: (renderProps: DeploymentProcessEditorRenderProps) => React.ReactNode;
    doBusyTask: DoBusyTask;
    isAddStepDisabled: boolean;
    id: string;
    scope: ActionScope;
}

type RouteProps = RouteComponentProps<{ projectSlug: string; stepId: string; filterKeyword: string }>;
type Props = RouteProps & DeploymentProcessEditorProps & WithProjectContextInjectedProps;

const DeploymentProcessLayoutOverflowMenu: React.FC = () => {
    const context = useDeploymentProcessContext();
    const projectContext = useProjectContext();

    const overFlowActions = [
        OverflowMenuItems.dialogItem("Reorder Steps", <DeploymentPartSorter title="Reorder Steps" deploymentProcessId={context.state.process && context.state.process.Id} onDeploymentProcessUpdated={context.actions.onDeploymentProcessChange} />, {
            permission: Permission.ProcessEdit,
            project: projectContext.state.model.Id,
            tenant: "*",
        }),
    ];

    return <OverflowMenu menuItems={overFlowActions} />;
};

class DeploymentProcessEditorLayout extends DataBaseComponent<Props, DeploymentProcessEditorState> {
    constructor(props: Props) {
        super(props);
        this.state = {
            redirectTo: null,
        };
    }

    render() {
        return (
            <ActionContextProvider doBusyTask={this.props.doBusyTask}>
                {({ state: { actionTemplates } }) => (
                    <DeploymentProcessContextProvider id={this.props.id} doBusyTask={this.props.doBusyTask}>
                        {processContext => {
                            const filter = processContext.state.filter;
                            const showExtendedFilterDetails = filter.channel || filter.environment || !filter.includeUnscoped;

                            return (
                                <FormPaperLayout
                                    title={"Process"}
                                    titlePath={routeLinks.project(this.props.match.params.projectSlug).process.root}
                                    busy={this.state.busy}
                                    errors={this.state.errors}
                                    model={null}
                                    cleanModel={null}
                                    saveButtonLabel={null}
                                    savePermission={{
                                        permission: Permission.ProcessEdit,
                                        project: this.props.projectContext.state.model.Id,
                                        tenant: "*",
                                    }}
                                    onSaveClick={null}
                                    primaryNavigationButton={this.addStepButton()}
                                    hideExpandAll={true}
                                >
                                    <Divider fullHeight={true} />
                                    <div className={styles.container}>
                                        {this.props.projectContext.state.deploymentProcess && this.props.projectContext.state.deploymentProcess.Steps.length > 0 && (
                                            <div className={styles.sidebar}>
                                                <>
                                                    <Section bodyClassName={styles.headerBody}>
                                                        <div className={styles.headerBodyFilter}>
                                                            <FilterSearchBox
                                                                hintText="Filter by name..."
                                                                value={filter.filterKeyword}
                                                                onChange={val => processContext.actions.onFilterChange(prev => ({ ...prev, filterKeyword: val }))}
                                                                fullWidth={true}
                                                                autoFocus={true}
                                                            />
                                                            <DeploymentProcessLayoutOverflowMenu />
                                                        </div>
                                                        {showExtendedFilterDetails && (
                                                            <div className={styles.regardingAnyContainer}>
                                                                <Note style={{ fontSize: "0.875rem" }}>
                                                                    {!!filter.environment && (
                                                                        <div>
                                                                            Environment: <strong>{filter.environment.Name}</strong>
                                                                        </div>
                                                                    )}
                                                                    {!!filter.channel && (
                                                                        <div>
                                                                            Channel: <strong>{filter.channel.Name}</strong>
                                                                        </div>
                                                                    )}
                                                                    <div>{filter.includeUnscoped ? "Including unscoped steps" : "Excluding unscoped steps"}</div>
                                                                </Note>
                                                                <div>
                                                                    <ActionButton type={ActionButtonType.Ternary} onClick={val => processContext.actions.onClearFilter()} label="Clear" />
                                                                </div>
                                                            </div>
                                                        )}
                                                    </Section>
                                                    <div className={deploymentPartForSidebarStyles.stepList}>
                                                        {processContext.filteredSteps.steps.length > 0 ? (
                                                            processContext.filteredSteps.steps
                                                                .filter(x => x.filtered)
                                                                .map(({ step: filteredStep, index }) => {
                                                                    const step = this.findStepByName(filteredStep.Name);
                                                                    if (!step) {
                                                                        Logger.log(`Failed to find step with name ${filteredStep.Name}`);
                                                                        return null;
                                                                    }
                                                                    return step.Actions.length === 1
                                                                        ? this.buildAction(this.props.scope, actionTemplates, filter, step, step.Actions[0], index)
                                                                        : this.buildParentStep(this.props.scope, actionTemplates, filter, step, index);
                                                                })
                                                        ) : (
                                                            <NoResults />
                                                        )}
                                                    </div>
                                                </>
                                            </div>
                                        )}
                                        <div className={styles.content}>{this.props.render({ step: null })}</div>
                                    </div>
                                </FormPaperLayout>
                            );
                        }}
                    </DeploymentProcessContextProvider>
                )}
            </ActionContextProvider>
        );
    }

    private findStepByName(name: string) {
        // We lookup by .Name due to cloned steps not having an ID initially.
        return this.props.projectContext.state.deploymentProcess.Steps.find(x => x.Name === name);
    }

    private buildParentStep(scope: ActionScope, actionTemplates: ActionTemplateSearchResource[], filter: DeploymentProcessEditorFilter, step: DeploymentStepResource, stepIndex: number) {
        const currentActionId = this.props.match.params.stepId;
        const isCurrentAction = currentActionId === step.Id;

        let disableActions = false;
        if (currentActionId && currentActionId !== step.Id) {
            // Is this the parent of a currently selected action?
            if (step.Actions.filter(x => x.Id === currentActionId).length > 0) {
                disableActions = true;
            }
        }
        const showWindowSize = step.Properties[SpecialVariables.Action.MaxParallelism] ? step.Properties[SpecialVariables.Action.MaxParallelism].toString().length > 0 : false;
        const parentStepLabel = showWindowSize ? (
            <span>Rolling deployment</span>
        ) : (
            <span>
                Multi-step deployment across
                <br />
                deployment targets
            </span>
        );

        return (
            <div key={step.Id} className={deploymentPartForSidebarStyles.group}>
                <DeploymentPartContextMenu
                    scope={this.props.scope}
                    keywordSearch={filter && filter.filterKeyword}
                    step={step}
                    stepIndex={stepIndex}
                    isParentGroup={true}
                    isCurrentAction={isCurrentAction}
                    render={renderProps => {
                        return <DeploymentPartForSidebar actionType={parentStepLabel} logoUrl={rollingStep} disableActionsDueToGroupEditing={disableActions} {...renderProps} />;
                    }}
                />
                {step.Actions.map((action, index) => this.buildAction(scope, actionTemplates, filter, step, action, index + 1, stepIndex, currentActionId))}
            </div>
        );
    }

    private buildAction(
        scope: ActionScope,
        actionTemplates: ActionTemplateSearchResource[],
        filter: DeploymentProcessEditorFilter,
        step: DeploymentStepResource,
        action: DeploymentActionResource,
        actionIndex: number,
        stepIndex?: number,
        currentActionId?: string
    ) {
        const isChildAction = !!stepIndex;
        const isCurrentAction = this.props.match.params.stepId === action.Id;
        let actionTypeName = action.ActionType;
        const actionTemplate = actionTemplates.find(x => x.Type === action.ActionType);
        if (actionTemplate) {
            actionTypeName = actionTemplate.Name;
        }

        let disableActions = false;
        if (isChildAction && currentActionId && currentActionId !== action.Id) {
            // Is the current action the parent step?
            if (step.Id === currentActionId) {
                disableActions = true;
            }
            // Is the current action a child of this parent step?
            if (step.Actions.filter(x => x.Id === currentActionId).length > 0) {
                disableActions = true;
            }
        }

        return (
            <DeploymentPartContextMenu
                scope={this.props.scope}
                key={action.Id}
                step={step}
                action={action}
                stepIndex={stepIndex}
                actionIndex={actionIndex}
                isChildAction={isChildAction}
                isCurrentAction={isCurrentAction}
                keywordSearch={filter && filter.filterKeyword}
                render={renderProps => {
                    return (
                        <DeploymentPartForSidebar key={action.Id} actionType={actionTypeName} logoUrl={getActionLogoUrl(action)} disableActionsDueToGroupEditing={disableActions} {...renderProps}>
                            {pluginRegistry.getAction(action.ActionType, scope).summary(action.Properties, isChildAction ? null : (step.Properties[SpecialVariables.Action.TargetRoles] as string), action.Packages, null)}
                        </DeploymentPartForSidebar>
                    );
                }}
            />
        );
    }

    private addStepButton() {
        return (
            <PermissionCheck permission={Permission.ProcessEdit} project={this.props.projectContext.state.model.Id} tenant="*">
                <NavigationButton type={NavigationButtonType.Secondary} label="Add Step" href={routeLinks.project(this.props.match.params.projectSlug).steptemplates} disabled={this.props.isAddStepDisabled} />
            </PermissionCheck>
        );
    }
}

const EnhancedDeploymentProcessEditorLayout = withProjectContext(DeploymentProcessEditorLayout);

export default EnhancedDeploymentProcessEditorLayout;
